import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Filter, RotateCcw, X } from 'lucide-react';
import { match } from '../api';
import type {
  MatchAccount,
  MatchAccountDetail,
  MatchLedgerRow,
} from '../api';
import { MatchShell } from './MatchShell';
import { MetricStrip, StickySummaryBar } from '../ui/mobileOps';
import {
  Button,
  EmptyState,
  ErrorBanner,
  Pill,
  Spinner,
} from '../ui/primitives';
import { SectionCard } from '../ui/components';

type PartKindFilter = 'all' | 'split' | 'remainder';

export function MatchAccountLedgerPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);

  const today = useMemo(() => todayIso(), []);
  const defaultFrom = useMemo(() => addDaysIso(today, -60), [today]);

  const [from, setFrom] = useState<string>(defaultFrom);
  const [to, setTo] = useState<string>(today);
  const [labelFilter, setLabelFilter] = useState<string>('');
  const [partKind, setPartKind] = useState<PartKindFilter>('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(false);

  const validId = Number.isFinite(id) && id > 0;

  const accountQuery = useQuery<MatchAccountDetail>({
    queryKey: ['match-account', id],
    queryFn: () => match.getAccount(id),
    enabled: validId,
  });

  const ledgerQuery = useQuery<{ rows: MatchLedgerRow[] }>({
    queryKey: ['match-ledger', id, from, to],
    queryFn: () => match.getLedger(id, { from, to, limit: 500 }),
    enabled: validId && !!from && !!to,
  });

  const accountsQuery = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts-all'],
    queryFn: () => match.listAccounts({ status: 'all' }),
  });

  const accountsById = useMemo(() => {
    const map = new Map<number, MatchAccount>();
    (accountsQuery.data ?? []).forEach((account) => map.set(account.id, account));
    return map;
  }, [accountsQuery.data]);

  const filteredRows = useMemo(() => {
    const rows = ledgerQuery.data?.rows ?? [];
    const needle = labelFilter.trim().toLowerCase();
    return rows.filter((row) => {
      if (partKind !== 'all' && row.part_kind !== partKind) return false;
      if (needle.length > 0) {
        const hay = (row.label ?? '').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [labelFilter, ledgerQuery.data, partKind]);

  const runningTotalByRowId = useMemo(() => {
    const balanceValue = accountQuery.data?.balance;
    const rows = ledgerQuery.data?.rows ?? [];
    const map = new Map<number, string>();
    if (!balanceValue || rows.length === 0) return map;
    const current = parseDecimalString(balanceValue);
    if (current === null) return map;
    let acc = current;
    for (const row of rows) {
      map.set(row.id, formatSignedKinr(acc));
      const amount = parseDecimalString(row.amount) ?? 0;
      acc = round3(acc - amount);
    }
    return map;
  }, [accountQuery.data?.balance, ledgerQuery.data]);

  const resetFilters = (): void => {
    setFrom(defaultFrom);
    setTo(today);
    setLabelFilter('');
    setPartKind('all');
  };

  if (!validId) {
    return (
      <MatchShell title="Match ledger">
        <SectionCard section="match">
          <ErrorBanner>Not a valid Match account id.</ErrorBanner>
        </SectionCard>
      </MatchShell>
    );
  }

  const account = accountQuery.data?.account;
  const balanceStr = accountQuery.data?.balance ?? '0';
  const isLoading = accountQuery.isLoading || ledgerQuery.isLoading;
  const ledgerError = ledgerQuery.error as Error | null;
  const accountError = accountQuery.error as Error | null;
  const activeFilters = activeFilterCount(from, to, defaultFrom, today, labelFilter, partKind);

  return (
    <MatchShell
      title={account?.name ?? 'Match ledger'}
      subtitle="Split output, source flow, and running balance"
      back={{ to: '/match/accounts', label: 'Accounts' }}
    >
      <div className="space-y-4">
        <div className="md:hidden">
          <StickySummaryBar
            section="match"
            stackedBelowTabs
            title={account?.name ?? 'Match ledger'}
            subtitle="Rows posted by the split engine for this account."
            metrics={[
              {
                label: 'Balance',
                value: formatKinrDisplay(balanceStr),
                tone: balanceStr.startsWith('-') ? 'neg' : balanceStr === '0' ? 'default' : 'pos',
              },
              { label: 'Rows', value: String(filteredRows.length) },
              { label: 'Filters', value: String(activeFilters) },
            ]}
          />
        </div>

        <div className="hidden md:block">
          <MetricStrip
            section="match"
            items={[
              {
                label: 'Current balance',
                value: formatKinrDisplay(balanceStr),
                note: 'K-INR',
                tone: balanceStr.startsWith('-') ? 'neg' : balanceStr === '0' ? 'default' : 'pos',
              },
              { label: 'Rows in view', value: String(filteredRows.length), note: 'after filters' },
              { label: 'Active filters', value: String(activeFilters), note: 'date, label, part kind' },
            ]}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/match/accounts/${id}`}
              className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-surface px-3 py-1.5 text-caption font-medium text-ink-muted hover:text-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
              Back to account
            </Link>
            {account?.status === 'archived' && <Pill tone="amber">Archived</Pill>}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-[16px] border border-border/70 bg-surface px-3 py-2 text-small font-medium text-ink shadow-sm backdrop-blur md:hidden"
            >
              <Filter className="h-4 w-4" strokeWidth={2} />
              Filters
              {activeFilters > 0 && <Pill section="match">{activeFilters}</Pill>}
            </button>
            <span className="text-caption text-ink-muted">{filteredRows.length} row{filteredRows.length === 1 ? '' : 's'}</span>
          </div>
        </div>

        {accountError && <ErrorBanner>Failed to load account: {accountError.message}</ErrorBanner>}
        {ledgerError && <ErrorBanner>Failed to load ledger: {ledgerError.message}</ErrorBanner>}

        <SectionCard section="match" title="Filters" className="hidden md:block">
          <FilterControls
            from={from}
            to={to}
            labelFilter={labelFilter}
            partKind={partKind}
            onFromChange={setFrom}
            onToChange={setTo}
            onLabelFilterChange={setLabelFilter}
            onPartKindChange={setPartKind}
            onReset={resetFilters}
          />
        </SectionCard>

        <SectionCard section="match" title="Ledger" padded={false}>
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-6 text-small text-ink-muted">
              <Spinner />
              Loading ledger…
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-3">
              <EmptyState
                section="match"
                icon={BookOpen}
                title="No ledger rows yet"
                description={
                  ledgerQuery.data && (ledgerQuery.data.rows.length ?? 0) > 0
                    ? 'No rows match your current filters.'
                    : 'Rows appear here once an entry posts against this account or one of its sources.'
                }
                action={
                  <Link to="/match/entry">
                    <Button size="sm">Create an entry</Button>
                  </Link>
                }
              />
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-xs">
                  <thead className="bg-surface-raised text-left text-[10px] uppercase tracking-wide text-ink-muted">
                    <tr>
                      <th className="px-4 py-2.5">Date</th>
                      <th className="px-4 py-2.5">Entry #</th>
                      <th className="px-4 py-2.5">Source account</th>
                      <th className="px-4 py-2.5">Label</th>
                      <th className="px-4 py-2.5">Part</th>
                      <th className="px-4 py-2.5 text-right">% used</th>
                      <th className="px-4 py-2.5 text-right">Amount</th>
                      <th className="px-4 py-2.5 text-right">Running</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredRows.map((row) => (
                      <LedgerDesktopRow
                        key={row.id}
                        row={row}
                        viewingAccountId={id}
                        sourceName={sourceDisplay(row.source_match_account_id, id, accountsById)}
                        running={runningTotalByRowId.get(row.id) ?? ''}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-3 p-3 md:hidden">
                {filteredRows.map((row) => (
                  <LedgerMobileCard
                    key={row.id}
                    row={row}
                    viewingAccountId={id}
                    sourceName={sourceDisplay(row.source_match_account_id, id, accountsById)}
                    running={runningTotalByRowId.get(row.id) ?? ''}
                  />
                ))}
              </div>
            </>
          )}
        </SectionCard>
      </div>

      {mobileFiltersOpen && (
        <div
          className="fixed inset-0 z-40 flex items-end bg-ink/60 md:hidden"
          onClick={() => setMobileFiltersOpen(false)}
        >
          <div
            className="w-full rounded-t-[28px] border-t border-border bg-surface p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-h2 font-semibold text-ink">Filters</h2>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                aria-label="Close filters"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-ink hover:bg-surface-raised"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            <FilterControls
              stackLayout
              from={from}
              to={to}
              labelFilter={labelFilter}
              partKind={partKind}
              onFromChange={setFrom}
              onToChange={setTo}
              onLabelFilterChange={setLabelFilter}
              onPartKindChange={setPartKind}
              onReset={resetFilters}
            />
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={() => setMobileFiltersOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </MatchShell>
  );
}

function FilterControls({
  from,
  to,
  labelFilter,
  partKind,
  onFromChange,
  onToChange,
  onLabelFilterChange,
  onPartKindChange,
  onReset,
  stackLayout = false,
}: {
  from: string;
  to: string;
  labelFilter: string;
  partKind: PartKindFilter;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onLabelFilterChange: (v: string) => void;
  onPartKindChange: (v: PartKindFilter) => void;
  onReset: () => void;
  stackLayout?: boolean;
}) {
  const wrapClass = stackLayout ? 'flex flex-col gap-3' : 'flex flex-wrap items-end gap-x-4 gap-y-3';
  return (
    <div className={wrapClass}>
      <label className="flex flex-col gap-1 text-caption text-ink-muted">
        <span>From</span>
        <input
          type="date"
          value={from}
          onChange={(e) => onFromChange(e.target.value)}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
      </label>
      <label className="flex flex-col gap-1 text-caption text-ink-muted">
        <span>To</span>
        <input
          type="date"
          value={to}
          onChange={(e) => onToChange(e.target.value)}
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
      </label>
      <label className="flex flex-col gap-1 text-caption text-ink-muted sm:min-w-[200px]">
        <span>Label contains</span>
        <input
          type="text"
          value={labelFilter}
          onChange={(e) => onLabelFilterChange(e.target.value)}
          placeholder="e.g. commission"
          className="rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
      </label>
      <div className="flex flex-col gap-1 text-caption text-ink-muted">
        <span>Part kind</span>
        <div className="inline-flex overflow-hidden rounded-md border border-border bg-surface">
          {(['all', 'split', 'remainder'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={() => onPartKindChange(kind)}
              className={
                'px-3 py-1.5 text-small capitalize transition-colors ' +
                (partKind === kind
                  ? 'bg-sec-match text-white'
                  : 'text-ink hover:bg-surface-raised')
              }
            >
              {kind === 'all' ? 'All' : kind === 'split' ? 'Splits only' : 'Remainder only'}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center gap-1 self-start rounded-md border border-border px-2.5 py-1.5 text-small text-ink hover:bg-surface-raised"
      >
        <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
        Reset filters
      </button>
    </div>
  );
}

function LedgerDesktopRow({
  row,
  viewingAccountId,
  sourceName,
  running,
}: {
  row: MatchLedgerRow;
  viewingAccountId: number;
  sourceName: string;
  running: string;
}) {
  const isReversal = row.reverses_entry_id !== null;
  const isRemainder = row.part_kind === 'remainder';
  const isSelfSource = row.source_match_account_id === viewingAccountId;
  const amountDisplay = formatKinrDisplay(row.amount);
  const pct = row.percentage_used !== null && row.percentage_used !== '' ? `${row.percentage_used}%` : '—';
  return (
    <tr className={isReversal ? 'text-ink-muted' : 'text-ink'}>
      <td className="px-4 py-2.5 whitespace-nowrap">{row.effective_date}</td>
      <td className="px-4 py-2.5 font-mono text-[11px]">
        <Link to={`/match/entries/${row.match_entry_id}`} className="text-sec-match hover:underline">
          #{row.match_entry_id}
        </Link>
      </td>
      <td className="px-4 py-2.5">
        <span className={isSelfSource ? 'italic text-ink-muted' : ''}>{sourceName}</span>
      </td>
      <td className="px-4 py-2.5">
        {isRemainder ? (
          <span className="italic text-ink-muted">— (remainder)</span>
        ) : (
          row.label ?? <span className="text-ink-muted">—</span>
        )}
      </td>
      <td className="px-4 py-2.5">
        <Pill tone={isRemainder ? 'amber' : 'neutral'}>{row.part_kind}</Pill>
      </td>
      <td className="px-4 py-2.5 text-right font-mono">{pct}</td>
      <td className="px-4 py-2.5 text-right font-mono">
        {isReversal ? (
          <span className="inline-flex items-center gap-1 text-ink-muted">
            <span aria-hidden>↺</span>
            <span className="line-through">{amountDisplay}</span>
          </span>
        ) : (
          amountDisplay
        )}
      </td>
      <td className="px-4 py-2.5 text-right font-mono text-ink-muted">{running}</td>
    </tr>
  );
}

function LedgerMobileCard({
  row,
  viewingAccountId,
  sourceName,
  running,
}: {
  row: MatchLedgerRow;
  viewingAccountId: number;
  sourceName: string;
  running: string;
}) {
  const isReversal = row.reverses_entry_id !== null;
  const isRemainder = row.part_kind === 'remainder';
  const isSelfSource = row.source_match_account_id === viewingAccountId;
  const amountDisplay = formatKinrDisplay(row.amount);
  const percentage = row.percentage_used !== null && row.percentage_used !== '' ? `${row.percentage_used}% used` : 'Remainder row';

  return (
    <div
      className={[
        'overflow-hidden rounded-[22px] border border-border/70 app-shell-surface-soft p-4 shadow-sm backdrop-blur',
        isReversal ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-small font-semibold tracking-[-0.01em] text-ink">
              {isRemainder ? 'Remainder' : row.label ?? 'Split row'}
            </div>
            <Pill tone={isRemainder ? 'amber' : 'neutral'}>{row.part_kind}</Pill>
            {isReversal && <Pill tone="neutral">Reversed</Pill>}
          </div>
          <div className="mt-1 text-[12px] text-ink-muted">{row.effective_date}</div>
          <div className="mt-2 text-[12px] text-ink-muted">
            {isSelfSource ? '(this account)' : sourceName}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`font-mono text-[1rem] font-semibold tabular-nums ${row.amount.startsWith('-') ? 'text-neg' : row.amount === '0' ? 'text-ink-muted' : 'text-pos'}`}>
            {isReversal ? `↺ ${amountDisplay}` : amountDisplay}
          </div>
          {running && (
            <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">bal {running}</div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-border/70 pt-3">
        <span className="text-caption text-ink-muted">{percentage}</span>
        <Link
          to={`/match/entries/${row.match_entry_id}`}
          className="inline-flex min-h-[36px] items-center rounded-full border border-border/70 bg-surface px-3 py-1.5 text-caption font-medium text-sec-match hover:bg-surface-raised"
        >
          Entry #{row.match_entry_id}
        </Link>
      </div>
    </div>
  );
}

function sourceDisplay(
  sourceId: number,
  viewingId: number,
  accountsById: Map<number, MatchAccount>,
): string {
  if (sourceId === viewingId) return '(this account)';
  const account = accountsById.get(sourceId);
  if (account) return account.name;
  return `#${sourceId}`;
}

function formatKinrDisplay(raw: string): string {
  const n = parseDecimalString(raw);
  if (n === null) return raw;
  return formatSignedKinr(n);
}

function formatSignedKinr(n: number): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const fixed = abs.toFixed(3);
  const [intPart, fracPart] = fixed.split('.');
  const withCommas = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${withCommas}.${fracPart ?? '000'}`;
}

function parseDecimalString(raw: string): number | null {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysIso(isoDate: string, delta: number): string {
  const parts = isoDate.split('-').map((s) => parseInt(s, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y === undefined || m === undefined || d === undefined) return isoDate;
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + delta);
  const yy = date.getUTCFullYear().toString().padStart(4, '0');
  const mm = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = date.getUTCDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function activeFilterCount(
  from: string,
  to: string,
  defaultFrom: string,
  defaultTo: string,
  labelFilter: string,
  partKind: PartKindFilter,
): number {
  let n = 0;
  if (from !== defaultFrom) n++;
  if (to !== defaultTo) n++;
  if (labelFilter.trim() !== '') n++;
  if (partKind !== 'all') n++;
  return n;
}
